import { useContext, useState, useMemo, useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import { BoxFull, Button } from "iuvity-ui-components";
import { useNavigate } from "react-router-dom";
import { AppContext } from "../../context/index";
import { SubheaderActions } from "iuvity-ui-components/dist/subheaderactions";
import { DataTable } from "iuvity-ui-components/dist/datatable";
import { InputText } from "iuvity-ui-components/dist/inputtext";
import { TableHeader } from "../datatable/TableHeader";
import { Actions } from "../datatable/Actions";
import { Column } from "primereact/column";
import { FormSkeleton } from "../skeleton/FormSkeleton";
import { FilterMatchMode } from "primereact/api";
import { CustomDialog, DialogProps } from "../dialog/CustomDialog";
import { formatStringToDate } from "../../helpers/formatDate";
import { useProtocols } from "../../hooks/useProtocols";
import { EmptyTemplate } from "../datatable/EmptyTemplate";
import noResultsImage from "../../assets/Illustration_NoResults.svg";
import reactiveIcon from "../../assets/icon-restore.svg";
import { Tag } from "iuvity-ui-components/dist/tag";

const formBlankValues = {
  name: "",
  description: "",
  status: "",
  createdAt: "",
  deletedAt: ""
};

export const ProtocolsAdminComponent: React.FC = () => {
  const navigate = useNavigate();
  const {
    t,
    appState: { language, pageFilter },
    setAppState,
  } = useContext(AppContext);

  const form = useForm({
    mode: "onChange",
    delayError: 1200,
    reValidateMode: "onChange",
    defaultValues: !!pageFilter
      ? pageFilter
      : formBlankValues,
  });

  const { control, setValue, getValues } = form;

  const columns = useMemo(() => {
    if (language) {
      return [
        {
          field: "name",
          header: t("Catalogue.nameLabel").split(' ')[0],
        },
        {
          field: "description",
          header: t("Catalogue.descriptionLabel"),
        },
        {
          field: "status",
          header: t("Catalogue.statusLabel"),
        },
        {
          field: "createdAt",
          header: t("Catalogue.createdAtLabel"),
        },
        {
          field: "deletedAt",
          header: t("Catalogue.deletedAtLabel"),
        },
        {
          field: "actions",
          header: t("Catalogue.actionsLabel"),
        },
      ];
    } else {
      return [];
    }
  }, [t, language]);

  const defaultFilterValues = {
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    name: { value: pageFilter?.name, matchMode: FilterMatchMode.CONTAINS },
    description: { value: pageFilter?.description, matchMode: FilterMatchMode.CONTAINS },
    status: { value: pageFilter?.status, matchMode: FilterMatchMode.CONTAINS },
    createdAt: { value: pageFilter?.createdAt, matchMode: FilterMatchMode.CONTAINS },
    deletedAt: { value: pageFilter?.deletedAt, matchMode: FilterMatchMode.CONTAINS },
  }
  const { data, protocolsAction, reloadFn } = useProtocols("DATA");
  const [selectedColumns, setSelectedColumns] = useState(columns);
  const [administrationFilters, setAdministrationFilters] = useState(defaultFilterValues);
  const [modalProps, setModalProps] = useState<DialogProps>({
    visible: false,
  } as DialogProps);

  const onAction = (type: "CLONE" | "UPDATE", data?: any) => {
    if (type === "UPDATE" && data) {
      setAppState((prev) => ({
        ...prev,
        selectedProtocolsObject: data,
      }));
      navigate("/catalogue/editprotocols");
    }
    if (type === "CLONE" && data) {
      setAppState((prev) => ({
        ...prev,
        selectedProtocolsObject: data,
      }));
      navigate("/catalogue/cloneprotocols");
    }
  };

  const onDeleteAction = async (data: any) => {
    const actionResult = await protocolsAction("DELETE", {id: data?.id, name: data?.name});
    if (actionResult === "SUCCESS") {
      reloadFn();
      setAdministrationFilters(defaultFilterValues);
    }
  };

  const onReactiveAction = async (data: any) => {
    const actionResult = await protocolsAction("REACTIVE", {id: data?.id, name: data?.name});
    if (actionResult === "SUCCESS") {
      reloadFn();
      setAdministrationFilters(defaultFilterValues);
    }
  };

  // ##### CAMBIAR LOS LABELS #####
  const handleModalProps = async (type: 'DELETE' | 'REACTIVE', data: any) => {
    setModalProps((prev:any) => ({
      ...prev,
      visible: true,
      title: type === 'DELETE' ? t("Catalogue.deleteModalProtocolsTitle") : t("Catalogue.reactiveModalProtocolsTitle"),
      content: `${t("Catalogue.modalProtocolsContent1Label")} "${
        data?.name
      }" ${type === 'DELETE' ? t("Catalogue.deleteModalProtocolsContent2Label") : t("Catalogue.reactiveModalProtocolsContent2Label")}`,
      onConfirm: () => {
        type === 'DELETE' ? onDeleteAction(data!) : onReactiveAction(data!);
      },
    }));
  };

  const onClearFilters = () => {
    setAdministrationFilters({
      global: { value: null, matchMode: FilterMatchMode.CONTAINS },
      name: { value: null, matchMode: FilterMatchMode.CONTAINS },
      description: { value: null, matchMode: FilterMatchMode.CONTAINS },
      status: { value: null, matchMode: FilterMatchMode.CONTAINS },
      createdAt: { value: null, matchMode: FilterMatchMode.CONTAINS },
      deletedAt: { value: null, matchMode: FilterMatchMode.CONTAINS },
    });
    setAppState((prev) => ({
      ...prev,
      pageFilter: null,
    }));
    form.reset(formBlankValues)
  };

  const onClearFiltersColumn = () => {
    setAdministrationFilters({
      global: { value: null, matchMode: FilterMatchMode.CONTAINS },
      name: { value: null, matchMode: FilterMatchMode.CONTAINS },
      description: { value: null, matchMode: FilterMatchMode.CONTAINS },
      status: { value: null, matchMode: FilterMatchMode.CONTAINS },
      createdAt: { value: null, matchMode: FilterMatchMode.CONTAINS },
      deletedAt: { value: null, matchMode: FilterMatchMode.CONTAINS },
    });
    setAppState((prev) => ({
      ...prev,
      pageFilter: null,
    }));
    form.resetField("name")
    form.resetField("description")
    form.resetField("status")
    form.resetField("createdAt")
    form.resetField("deletedAt")
  };

  useEffect(() => {
    if (language)
      setSelectedColumns((prev) =>
        prev.map((col) => ({
          field: col.field,
          header: col.field === 'name'
            ? t(`Catalogue.${col.field}Label`).split(' ')[0]
            : t(`Catalogue.${col.field}Label`),
        }))
      );
  }, [t, language, setSelectedColumns]);

  // ##### CAMBIAR LAS LABELS #####
  const filterTemplate = (options: any) => {
    return (
      <Controller
          name={options.field}
          control={control}
          render={({ field }) => {
            field.onChange = (e) => {
              setValue(field.name, e.target.value);
              options.filterCallback(e.target.value);
            };
            return (
              <>
                <label htmlFor={options.field} className="block lb mt-2 mb-2">
                  {t(`Catalogue.${options.field}Label`)}
                </label>
                <InputText
                  placeholder={t(`Catalogue.${options.field}FilterPlaceholder`)} 
                  autoComplete="off"
                  type={'text'}
                  className="mb-2"
                  {...field}
                />
              </>
            );
          }}
      />
    )
  }

  const filterClearTemplate = (options: any) => {
      return (
        <Button
          label={t("ValidationMessages.clearFilterLabel")}
          variant="p-button-text"
          onClick={onClearFiltersColumn}
        />
      )
  }
    
  const filterApplyTemplate = (options: any) => {
      return (
        <Button
          label={t("ValidationMessages.applyFilterLabel")}
          className="p-button"
          onClick={()=>{
            options.filterApplyCallback();
            setAppState((prev: any)=>({
              ...prev,
              pageFilter: getValues(),
            }));
          }}
        />
      )
  }

  const statusBodyTemplate = (rowData:any) => (
    <Tag
      value={rowData.status}
      severity={
        (['active', 'activo'].includes(rowData.status.toLowerCase()) && 'success') ||
        (['draft', 'borrador'].includes(rowData.status.toLowerCase()) && 'info') ||
        (['deleted', 'eliminado'].includes(rowData.status.toLowerCase()) && 'danger') ||
        'warning'
      }
    />
  )

  return (
    <>
      <div className="points__tabdescription">
        <span>{t("Catalogue.protocolsDescription")}</span>
      </div>
      <BoxFull className="ml-0 mt-0 mx-0 px-0" variant="tertiary">
        <div className="tablefilter-mc2">
            <Button
              className="hover"
              label={t("Catalogue.clearFilterBtnLabel")}
              variant="p-button"
              icon="iu iu-icon-error"
              onClick={onClearFilters}
            />
            <SubheaderActions
              className="p-0 m-0"
              actions={[
                {
                  id: "create-protocol",
                  icon: "iu iu-icon-plus",
                  label: t("Catalogue.newProtocolsBtnLabel"),
                  onClick: () => navigate("/catalogue/newprotocols"),
                },
              ]}
            />
        </div>
      </BoxFull>
      <BoxFull className="dt-c mx-0" variant="tertiary" style={{ backgroundColor: 'var(--cardBackgroundColorOne)' }}>
        {data ? (
          <DataTable
            header={
              <TableHeader
              title={t("Catalogue.protocolsTabTitle")}
              columns={columns}
              selectedColumns={selectedColumns}
              toggleColumns={setSelectedColumns}
              filters={administrationFilters}
              setFilters={setAdministrationFilters}
              reload={reloadFn}
              />
            }
            currentPageReportTemplate={t(
              "ConsoleTranslations.rowsPaginatorTemplateLabel"
            )}
            paginatorRight={t('ConsoleTranslations.itemsTextDatatableLabel')}
            emptyMessage={<EmptyTemplate image={noResultsImage} text={t('Catalogue.emptyDataTableMessage')} />}
            rows={10}
            size="normal"
            filters={administrationFilters}
            sortField="updatedAt"
            dataKey="protocols-id"
            sortOrder={-1}
            value={data?.map((protocolItem: any) => ({
              ...protocolItem,
              status: t(`Catalogue.${protocolItem?.status?.name}Label`),
              createdAt: formatStringToDate(protocolItem?.createdAt, true, t),
              deletedAt: !!protocolItem?.deletedAt ? formatStringToDate(protocolItem?.deletedAt, true, t) : '-',
              actions: !protocolItem?.deletedAt
              ? (
                  <Actions
                    enableDelete
                    auxButtons={[{
                      label: t("Catalogue.cloneBtnLabel"),
                      icon: "iu-icon-copy",
                      function: () => onAction("CLONE", protocolItem),
                      color: "var(--successColor)",
                      order: "-1",
                    }]}
                    onEdit={() => onAction("UPDATE", protocolItem)}
                    onDelete={() => handleModalProps('DELETE', protocolItem)}
                  />
                )
              : (
                <Button
                  className="p-button-rounded p-button-success p-button-outlined act-btn"
                  aria-label="reactive"
                  tooltip={t("Tenants.reactiveLabel")}
                  tooltipOptions={{ position: "bottom" }}
                  variant="p-button"
                  onClick={() => handleModalProps('REACTIVE', protocolItem)}
                  style={{
                    color: "var(--successColor)",
                  }}
                >
                  <img
                    alt='reactive-icon'
                    src={reactiveIcon}
                    width='15px'
                  />
                </Button>
              )
                ,
              }))}
            scrollable
            resizableColumns
            globalFilterFields={[
              "name",
              "description",
              "status",
              "createdAt",
              "deletedAt",
            ]}
            filterDisplay="menu"
            rowsPerPageOptions={[10,20,50]}
            >
            {selectedColumns.map((col: any) => {
              return (
                <Column
                  key={col.field}
                  field={col.field}
                  header={col.header}
                  sortable={!(col.field === "actions")}
                  align={col.field === "actions"  ? "center" : "left"}
                  headerStyle={
                    col.field === "actions" 
                    ? { justifyContent: "center" }
                    : undefined
                  }
                  style={{
                    boxShadow:
                    col.field === "actions"
                    ? "rgb(230 230 230 / 55%) -2px 0px, rgb(235 235 235 / 55%) -3px 0px, rgb(240 240 240 / 45%) -5px 0px"
                    : undefined,
                    maxWidth: col.field === "actions" ? "8.25rem" : "100%",
                    whiteSpace: "normal",
                    // minWidth: "10rem",
                  }}
                  frozen={col.field === "actions" ? true : false}
                  alignFrozen={col.field === "actions" ? "right" : undefined}
                  filterField={col.field}
                  showFilterMatchModes={false}
                  filterMenuStyle={{ width: "23rem" }}
                  filter={col.field === "actions" ? false : true}
                  filterElement={filterTemplate}
                  filterApply={filterApplyTemplate}
                  filterClear={filterClearTemplate}
                  body={col.field === "status" ? statusBodyTemplate : undefined}
                />
                );
              })}
          </DataTable>
        ) : (
          <FormSkeleton length={8} />
          )}
      </BoxFull>
      <CustomDialog
        title={modalProps.title}
        content={modalProps.content}
        visible={modalProps.visible}
        handleModalState={setModalProps}
        onConfirm={modalProps.onConfirm}
      />
    </>
  );
};
